import { getLoginServiceData, getSelectedProductValues, isOnlyNoReturnComplaintsSelected } from "./appCore";
import { isNull, isNumber } from "lodash"; //

export const SHIPMENT_SOLUTION_TYPES = {
    PICKUP: {
        key: "PICKUP",
    },
    MANUAL: {
        key: "MANUAL",
    },
    PRINT_LABEL: {
        key: "PRINT_LABEL",
    },
    RETURN_IN_STORE: {
        key: "RETURN_IN_STORE",
    },
    RETURN_SLIP: {
        key: "RETURN_SLIP",
    },
    PRINT_LABEL_MANUAL_UPLOAD: {
        key: "PRINT_LABEL_MANUAL_UPLOAD",
    },
    LOCKER: {
        key: "LOCKER",
    },
    NO_RETURN_NEEDED: {
        key: "NO_RETURN_NEEDED",
    },
    REGISTER_WITHDRAWAL: {
        key: "REGISTER_WITHDRAWAL",
    },
};

export function getSelectedShipmentSolution(appProps) {
    return isOnlyNoReturnComplaintsSelected(appProps) ? { type: SHIPMENT_SOLUTION_TYPES.NO_RETURN_NEEDED.key } : getAvailableShipmentSolutions(appProps)[getSelectedShipmentSolutionIndex(appProps)];
}

export function onlyOneSimpleShipmentSolutionAvailable(appProps) {
    const solutions = getAvailableShipmentSolutions(appProps);
    return solutions.length === 1 && !shipmentMethodRequiresMultipleSelection(solutions[0]);
}

export function getAvailableShipmentSolutions(appProps) {
    return getLoginServiceData(appProps)?.shipmentSolutions || [];
}

export function getSelectedShipmentSolutionData(appProps) {
    return appProps?.selectedShipmentSolutionData;
}

export function getSelectedShipmentSolutionIndex(appProps) {
    return getSelectedShipmentSolutionData(appProps)?.index;
}

export function getSelectedShipmentSolutionMethodOption(appProps) {
    return getAvailableShipmentSolutions(appProps)?.[getSelectedShipmentSolutionIndex(appProps)];
}

export function getSelectedShipmentSolutionDateIndex(appProps) {
    return getSelectedShipmentSolutionData(appProps)?.dateIndex;
}

export function isAnyShipmentSolutionSelected(appProps) {
    return isNumber(getSelectedShipmentSolutionData(appProps));
}

export function isPickupDateSelectionAllowed(appProps) {
    return getLoginServiceData(appProps)?.pickupDateSelectionAllowed;
}

export function shipmentMethodDateOnly(appProps) {
    return isPickupDateSelectionAllowed(appProps) && getAvailableShipmentSolutions(appProps).length === 0;
}

export function isResaleActive(appProps) {
    return !!getLoginServiceData(appProps)?.resaleActive;
}

export function isShipmentMethodPageEnabled(appProps) {
    const solutions = getAvailableShipmentSolutions(appProps);
    if (solutions.length === 0) {
        return false;
    }

    if (solutions.length === 1 && solutions[0]?.type === SHIPMENT_SOLUTION_TYPES.REGISTER_WITHDRAWAL.key) {
        return false;
    }

    if (isOnlyNoReturnComplaintsSelected(appProps)) {
        return false;
    }

    if (getSelectedProductValues(appProps)?.length === 0) {
        return true;
    }

    if (isPickupDateSelectionAllowed(appProps)) {
        return true;
    }

    return true;
}

export function isShipmentMethodInputsValid(appProps) {
    const selectedShipmentMethod = getSelectedShipmentSolutionMethodOption(appProps);
    if (isShipmentMethodWithOptions(selectedShipmentMethod)) {
        return getSelectedShipmentSolutionData(appProps)?.foreignOptionId;
    }

    return (shipmentMethodDateOnly(appProps) && !isNull(getSelectedShipmentSolutionDateIndex(appProps))) || (!shipmentMethodDateOnly(appProps) && !!getSelectedShipmentSolutionData(appProps));
}

export function isDateSelectorShipmentMethod(shipmentMethod) {
    return shipmentMethod?.type === SHIPMENT_SOLUTION_TYPES.PICKUP.key;
}

export function getShipmentMethodOptions(shipmentMethod) {
    return shipmentMethod?.availableOptions?.options;
}

export function isShipmentMethodWithOptions(shipmentMethod) {
    return getShipmentMethodOptions(shipmentMethod)?.length > 0;
}

export function shipmentMethodRequiresMultipleSelection(shipmentMethod) {
    return isDateSelectorShipmentMethod(shipmentMethod) || isShipmentMethodWithOptions(shipmentMethod);
}

export function getShipmentCarrierData(shipmentMethod) {
    return shipmentMethod?.carrierData;
}

export function getCarrierOrSubCarrierData(shipmentMethod) {
    return shipmentMethod?.subCarrierData || shipmentMethod?.carrierData;
}

export function getShipmentSolutionData(shipmentMethod) {
    return SHIPMENT_SOLUTION_TYPES[shipmentMethod?.type];
}

export function getShipmentCarrier(shipmentMethod) {
    return shipmentMethod?.carrier;
}

export function getShipmentOptionData(shipmentMethod, { appProps }) {
    function getShipmentMethodSolutionTitle(shipmentSolutionType, carrierName) {
        return (
            appProps?.getLocaleText(`shipment-method-solution-title.${shipmentSolutionType}.${carrierName}`) ||
            appProps?.getLocaleText(`shipment-method-solution-title.${shipmentSolutionType}.default`) ||
            appProps?.getLocaleText(`shipment-method-solution-title.${shipmentSolutionType}`)
        );
    }

    function getShipmentMethodSolutionTitleCarrier(shipmentMethod) {
        return `shipment-method-solution-title-carrier.${getShipmentCarrier(shipmentMethod)}`;
    }

    function getShipmentMethodSolutionSubTitleCarrier(shipmentMethod) {
        return `shipment-method-solution-subtitle-carrier.${getShipmentCarrier(shipmentMethod)}`;
    }

    if (shipmentMethod?.type === SHIPMENT_SOLUTION_TYPES.MANUAL.key) {
        return {
            title: appProps?.getLocaleText("manual-shipping-option"),
            icon: "/icons/delivery/manual.png",
        };
    }
    if (shipmentMethod?.type === SHIPMENT_SOLUTION_TYPES.PRINT_LABEL_MANUAL_UPLOAD.key) {
        return {
            title: appProps?.getLocaleText("manual-upload-shipping-option"),
            icon: "/icons/delivery/return_slip.webp",
        };
    }
    if (shipmentMethod?.type === SHIPMENT_SOLUTION_TYPES.RETURN_SLIP.key) {
        return {
            title: appProps?.getLocaleText("return-slip-shipping-option"),
            icon: "/icons/delivery/return_slip.webp",
        };
    }
    if (shipmentMethod?.type === SHIPMENT_SOLUTION_TYPES.RETURN_IN_STORE.key) {
        return {
            title: appProps?.getLocaleText("shipment-method-solution-title." + shipmentMethod?.type),
            subTitle: appProps?.getLocaleText("shipment-method-solution-subtitle." + shipmentMethod?.type),
            icon: "/icons/delivery/in_store.png",
        };
    }

    const shipmentCarrierData = getShipmentCarrierData(shipmentMethod);
    const shipmentSolutionData = getShipmentSolutionData(shipmentMethod);

    const shipmentSolutionName = getShipmentMethodSolutionTitle(shipmentSolutionData?.key, shipmentMethod?.carrier) || shipmentSolutionData?.key;

    const shipmentCarrierOverrideTitle = appProps?.getLocaleText(getShipmentMethodSolutionTitleCarrier(shipmentMethod));
    const shipmentCarrierOverrideSubTitle = appProps?.getLocaleText(getShipmentMethodSolutionSubTitleCarrier(shipmentMethod));

    const title = shipmentCarrierOverrideTitle || (shipmentCarrierData?.displayName ? shipmentSolutionName : null);
    const subTitle = shipmentCarrierOverrideSubTitle || shipmentCarrierData?.displayName || shipmentSolutionName;

    return {
        title: title || subTitle,
        subTitle: title ? subTitle : null,
    };
}

export function getCarrierColor(carrierData) {
    return carrierData?.color || "transparent";
}
